import React from 'react';
import { useLandingSections, type LandingSection } from '@/hooks/useLandingSections';
import { Section } from '@/pages/LandingComplete';
import SectionHeader from './SectionHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, Clock, Shield, MapPin, Users, Laptop2 } from 'lucide-react';

interface WhyContent {
  title?: string;
  titleHighlight?: string;
  subtitle?: string;
  items?: Array<{
    icon?: string;
    title: string;
    description: string;
  }>;
}

const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  MapPin,
  Clock,
  Shield,
  Users,
  Laptop2,
  CheckCircle,
};

export function DynamicWhy({ content }: { content: WhyContent }) {
  const items = content.items || [];
  const icons = [CheckCircle, Clock, Shield, MapPin, Users, Laptop2];

  return (
    <Section id="warum" alternate>
      <SectionHeader 
        number="01" 
        title={content.title || "Warum"} 
        highlight={content.titleHighlight || "Klartech Müritz?"}
        subtitle={content.subtitle || "Ihr vertrauensvoller IT-Partner"}
      />
      
      <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item, i) => {
          const IconComponent = item.icon && iconMap[item.icon] ? iconMap[item.icon] : icons[i % icons.length];
          return (
            <div
              key={item.title + i}
              className="group relative rounded-3xl border border-navy/10 bg-card p-8 shadow-soft transition-all duration-300 hover:shadow-lift hover:border-primary/35 hover:-translate-y-1.5"
            >
              {/* Leiterbahn-Punkt */}
              <span className="absolute top-6 right-6 h-2.5 w-2.5 rounded-full border-2 border-flame/45 transition-colors group-hover:border-flame" aria-hidden="true" />

              <div className="absolute -top-6 left-8 z-10">
                <div className="inline-flex rounded-2xl bg-navy p-4 shadow-soft transition-colors duration-300 group-hover:bg-primary">
                  <IconComponent size={24} className="text-primary-foreground" />
                </div>
              </div>
              <div className="pt-6 relative z-20">
                <h3 className="text-lg font-bold text-navy mb-3">{item.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{item.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}
